import * as types from './types'
import { type Element, Group, Image as ZImage, init } from 'zrender'
import { MaybeRef, useElementSize } from '@vueuse/core'
import { ReactiveParam, reactiveParam } from '@/utils/reactive-param'
import { ref, unref, watchEffect } from 'vue'

export type CreateParams<T extends Tile> = {
    tile: T
    width: number
    instance: ReturnType<typeof init>
    refresh: () => void
}
export type Creators = typeof types
export type CreatorKey<T extends Tile> = {
    [K in keyof Creators]: Creators[K] extends Creator<T> ? K : never
}[keyof Creators]
export type Creator<T extends Tile> = (params: CreateParams<T>) => Element[]

/**
 * Create the tile canvas instance.
 */
export function useTile<T extends Tile>(
    tile: ReactiveParam<T>,
    container?: MaybeRef<HTMLElement | undefined>
) {
    /**
     * SVG string.
     */
    const svg = ref<string>()

    /**
     * Get the width of the container.
     */
    const size = useElementSize(container)

    /**
     * Reactive tile.
     */
    const tileRef = reactiveParam(tile)

    /**
     * Main group.
     */
    const groupRef = ref<Group>()

    /**
     * ZRender instance.
     */
    const instance = init(document.createElement('div'), {
        renderer: 'svg',
        ssr: true
    })

    /**
     *  Generate the tile.
     */
    function generate() {
        const tile = unref(tileRef)

        const width = unref(size.width)

        if (!width) return

        instance.clear()
        groupRef.value = new Group()
        const group = unref(groupRef.value)

        if (tile.type === 'svg') {
            const url =
                'url' in tile
                    ? tile.url
                    : `data:image/svg+xml;base64,${btoa(tile.content)}`
            const image = new Image()
            image.src = url
            image.crossOrigin = 'anonymous'
            image.onload = () => {
                img.setStyle({ width: image.width, height: image.height })
                instance.resize({ width, height: image.height })
                updateString()
            }
            const img = new ZImage({
                style: { image, width }
            })
            group.add(img)
        } else {
            const create = types[tile.type] as Creator<T>
            instance.resize({ width, height: 0 })

            const elements = create({
                width,
                tile,
                instance,
                refresh: updateString
            })

            if (!elements) return

            elements.forEach(element => group.add(element))

            const height = group.getBoundingRect().height
            instance.resize({ height, width })
        }

        instance.add(group)
        updateString()
    }

    /**
     * Update the SVG string.
     */
    function updateString() {
        if (!instance.getWidth() || !instance.getHeight()) return

        unref(groupRef.value)?.update()

        const value = instance.painter.renderToString?.()

        svg.value = value

        return value
    }

    watchEffect(generate)

    return { svg, generate, instance }
}
